import _styled, { default as _styled2, default as _styled3 } from "styled-components";
import { graphql, useFragment, type FragmentType } from "~/gql/runtime";
import { Link } from "~/features/ui/link";
import { takeFirst } from "~/features/app/utils";
import { IconButton } from "~/features/ui/icon-button";
import { type Mode, type Theme } from "~/features/ui/get-theme-style";
import { isLinkValid } from "~/features/ui/link/utils";
import ArrowRight from "~/public/icons/arrow-right.svg";
import ArrowTopRight from "~/public/icons/arrow-top-right.svg";
const fragment = graphql(`
  fragment LinkBarsItemLink on blocks_linkBarsItemLink_BlockType {
    id
    caption: baseText
    link: baseLink {
      ...Link
    }
  }
`);
type Props = FragmentType<typeof fragment> & {
  mode: Mode;
  theme: Theme;
};
type PlainLink = Record<string, unknown> & {
  href?: string;
  newWindow?: boolean;
  text?: React.ReactNode;
};
export type LinkBarsItemLinkViewProps = {
  caption?: string | null;
  id?: string | null;
  link?: PlainLink | Array<PlainLink | null | undefined> | null;
  mode: Mode;
  theme: Theme;
};
function getPlainLinkIcon(link?: PlainLink | null) {
  if (!link) {
    return undefined;
  }
  return link.newWindow ? ArrowTopRight : ArrowRight;
}
export function LinkBarsItemLinkView({
  caption,
  link: linkValue,
  id
}: LinkBarsItemLinkViewProps) {
  const link = takeFirst(linkValue);
  const icon = getPlainLinkIcon(link);
  const hoverId = `id-${id}`;
  if (!isLinkValid(link)) {
    return null;
  }
  return <_StyledLink {...link} id={hoverId}>
      {/* v3: bars sit on the white section as grey-20 (#EFEFF6) cards. */}
      <_StyledDiv>
        {"text" in link && <_StyledSpan>{link.text as string}</_StyledSpan>}
        {caption && <span>{caption}</span>}
      </_StyledDiv>
      {icon && <IconButton as="div" icon={{
      component: icon
    }} fullHeight hoverId={hoverId} />}
    </_StyledLink>;
}
export function LinkBarsItemLink(props: Props) {
  const {
    mode,
    theme,
    ...rest
  } = props;
  const {
    caption,
    link,
    id
  } = useFragment(fragment, rest);
  return <LinkBarsItemLinkView caption={caption} id={id} link={link as LinkBarsItemLinkViewProps["link"]} mode={mode} theme={theme} />;
}
var _StyledLink = _styled(Link).withConfig({
  componentId: "sc-sf-1nxk1vr-0"
})({
  "display": "flex",
  "overflow": "hidden",
  "borderRadius": "2px"
});
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-1nxk1vr-1"
})({
  "position": "relative",
  "zIndex": "10",
  "display": "flex",
  "flex": "1 1 0%",
  "flexDirection": "column",
  "rowGap": "0.8rem",
  "backgroundColor": "var(--bg-secondary)",
  "padding": "1.6rem",
  "fontSize": "1.6rem",
  "lineHeight": "1.5",
  "fontWeight": "400",
  "color": "var(--text-primary)",
  "@media (min-width: 1024px)": {
    "padding": "2.4rem",
    "fontSize": "1.8rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.018rem",
    "fontWeight": "400"
  }
});
var _StyledSpan = /*#__PURE__*/_styled3("span").withConfig({
  displayName: "link-bars-itemlink___StyledSpan",
  componentId: "sc-avtkb4-0"
})({
  "fontWeight": "500"
});